import { useState } from "react";
import { api } from "../../../lib/api";
import { toast } from "sonner";
import { appConfirm } from "../../../components/ConfirmDialog";
import { CheckCircle2, XCircle } from "lucide-react";
import { QuoteContactCard } from "../QuoteContactCard";

const gbp = (n) => "£" + Math.round(Number(n || 0)).toLocaleString("en-GB");
const dateGB = (d) => d ? new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—";
const dateTimeGB = (d) => d ? new Date(d).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

export function QuoteOverview({ quoteId, data, setData, onChanged, onClose }) {
  const [busy, setBusy] = useState(false);

  const markDead = async () => {
    if (!(await appConfirm({ title: "Move to Dead leads?", description: "The client drops off the live pipeline and auto-purges after 60 days. Restorable from the Dead leads filter.", confirmLabel: "Mark dead", destructive: true }))) return;
    setBusy(true);
    try {
      await api.post(`/admin/saved-quotes/${encodeURIComponent(quoteId)}/mark-dead`);
      toast.success("Moved to Dead leads");
      if (onChanged) onChanged();
      if (onClose) onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not mark as dead lead"); }
    setBusy(false);
  };

  return (
    <div className="space-y-4" data-testid="quote-overview">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <QuoteContactCard quoteId={quoteId} data={data}
          onSaved={(patch) => { setData(d => d ? { ...d, ...patch } : d); if (onChanged) onChanged(); }} />
        <div className="border border-[#E5E7EB] bg-white p-4">
          <div className="label-eyebrow mb-2">Total (as saved)</div>
          <div className="text-2xl font-black text-[#12263A]" data-testid="quote-detail-total">{gbp(data.total_inc_vat)}</div>
          <div className="text-xs text-[#6B7280] mt-1">Incl. VAT</div>
          {data.breakdown && data.breakdown.total_inc_vat && Math.abs(data.breakdown.total_inc_vat - data.total_inc_vat) > 1 && (
            <div className="text-[11px] text-[#C0392B] mt-1.5">Re-quoted today: {gbp(data.breakdown.total_inc_vat)}</div>
          )}
        </div>
        <div className="border border-[#E5E7EB] bg-white p-4">
          <div className="label-eyebrow mb-2">Dates</div>
          <div className="text-xs text-[#6B7280]">Created: <b className="text-[#12263A]">{dateTimeGB(data.created_at)}</b></div>
          {data.updated_at && data.updated_at !== data.created_at && (
            <div className="text-xs text-[#6B7280] mt-1">Updated: <b className="text-[#12263A]">{dateGB(data.updated_at)}</b></div>
          )}
          <div className="text-xs text-[#6B7280] mt-1">Valid until: <b className="text-[#12263A]">{dateGB(data.valid_until)}</b></div>
        </div>
      </div>

      {data.spec_lines && data.spec_lines.length > 0 && (
        <div className="border border-[#E5E7EB] bg-white p-4">
          <div className="label-eyebrow mb-3">Scope of works (what the client saw)</div>
          <ul className="space-y-1.5">
            {data.spec_lines.map((l, i) => (
              <li key={`${i}-${l}`} className="flex gap-2 text-sm text-[#374151]" data-testid={`quote-detail-spec-${i}`}>
                <CheckCircle2 size={15} className="text-[#C79A3B] mt-0.5 shrink-0" /><span>{l}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {data.route === "planning" && (data.plan_analysis || data.plan_answers) && (
        <div className="border border-[#E5E7EB] bg-white p-4" data-testid="quote-detail-planning-info">
          <div className="label-eyebrow mb-3">Planning-route submission</div>
          {data.has_full_plans !== undefined && (
            <div className="text-xs text-[#6B7280] mb-3">
              Client uploaded {data.has_full_plans ? "a full plans set (no plan-gen fee added)" : "sketches only — plan-gen fee applied"}
              {data.plan_generation_fee > 0 && <> · plan-gen fee <b className="text-[#12263A]">{gbp(data.plan_generation_fee)}</b></>}
            </div>
          )}
          {data.plan_analysis && Object.keys(data.plan_analysis).length > 0 && (
            <details className="mb-3" data-testid="quote-detail-plan-analysis">
              <summary className="text-xs font-bold uppercase tracking-widest text-[#12263A] cursor-pointer hover:text-[#C79A3B]">AI-analysed spec (from uploaded plans)</summary>
              <pre className="mt-2 text-[11px] text-[#374151] bg-[#F8F5F0] p-3 border border-[#F0EAE0] whitespace-pre-wrap break-words font-mono max-h-96 overflow-y-auto">{JSON.stringify(data.plan_analysis, null, 2)}</pre>
            </details>
          )}
          {data.plan_answers && Object.keys(data.plan_answers).length > 0 && (
            <details data-testid="quote-detail-plan-answers">
              <summary className="text-xs font-bold uppercase tracking-widest text-[#12263A] cursor-pointer hover:text-[#C79A3B]">Customer&apos;s answers to the planning questions</summary>
              <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1 text-xs">
                {Object.entries(data.plan_answers).map(([k, v]) => (
                  <div key={k} className="border-b border-[#F0EAE0] py-1.5">
                    <div className="text-[10px] uppercase tracking-widest text-[#6B7280]">{k.replace(/_/g, " ")}</div>
                    <div className="text-[#12263A] font-medium">{typeof v === "object" ? JSON.stringify(v) : String(v || "—")}</div>
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>
      )}

      {data.route === "pd" && data.config && Object.keys(data.config).length > 0 && (
        <div className="border border-[#E5E7EB] bg-white p-4" data-testid="quote-detail-config-info">
          <div className="label-eyebrow mb-3">PD-route configuration (what the client picked)</div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1 text-xs">
            {Object.entries(data.config).filter(([, v]) => v !== null && v !== "" && !(typeof v === "object" && !Array.isArray(v) && Object.keys(v).length === 0)).slice(0, 40).map(([k, v]) => (
              <div key={k} className="border-b border-[#F0EAE0] py-1.5">
                <div className="text-[10px] uppercase tracking-widest text-[#6B7280]">{k.replace(/_/g, " ")}</div>
                <div className="text-[#12263A] font-medium truncate">{typeof v === "object" ? JSON.stringify(v) : String(v)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {data.version_history && data.version_history.length > 1 && (
        <div className="border border-[#E5E7EB] bg-white p-4">
          <div className="label-eyebrow mb-2">Version history</div>
          <div className="text-xs text-[#6B7280] space-y-1">
            {data.version_history.map(v => (
              <div key={v.version} data-testid={`quote-detail-version-${v.version}`}>v{v.version} · <b className="text-[#12263A]">{gbp(v.total_inc_vat)}</b> · {dateGB(v.saved_at)}</div>
            ))}
          </div>
        </div>
      )}

      <div className="border border-[#F5C7C0] bg-[#FEF6F5] p-4">
        <div className="label-eyebrow text-[#8A2A1E] mb-1">Client doesn&apos;t wish to proceed</div>
        <p className="text-[11px] text-[#6B7280] mb-3">Moves this file to <b>Dead leads</b> — no further automated emails, auto-purged after 60 days, restorable until then.</p>
        <button data-testid="quote-detail-mark-dead" onClick={markDead} disabled={busy}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold border border-[#C0392B] text-[#C0392B] hover:bg-[#C0392B] hover:text-white disabled:opacity-50">
          <XCircle size={13} /> Not proceeding — move to Dead leads
        </button>
      </div>
    </div>
  );
}
